import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { createApiClient } from "../api/apiClient";
import {
  initChatMessage,
  openChatStream,
  replayThread,
  listThreads,
  searchThreads,
  updateThread,
  deleteThread,
  ChatMessageDto,
  ChatThreadDto,
  ChatStreamEvent,
  InitResponse,
  StreamHandle,
  ThreadsPage,
  ReplayPage,
} from "../api/chatApi";

export interface ChatMessage extends ChatMessageDto {
  status?: "pending" | "complete" | "error";
  isOptimistic?: boolean;
}

export type ChatThread = ChatThreadDto;

const buildChatClient = (token?: string) => createApiClient(token);

const TITLE_POLL_MAX_ATTEMPTS = 20;
const TITLE_POLL_INTERVAL_MS = 500;

const pollUntilThreadTitle = async (
  queryClient: QueryClient,
  token: string | undefined,
  threadUuid: string,
): Promise<void> => {
  const client = buildChatClient(token);

  for (let attempt = 0; attempt < TITLE_POLL_MAX_ATTEMPTS; attempt++) {
    const page = await listThreads(client, { archived: false });
    const thread = page.threads.find((t) => t.uuid === threadUuid);

    if (thread?.title) {
      await queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
      return;
    }

    if (attempt < TITLE_POLL_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) =>
        setTimeout(resolve, TITLE_POLL_INTERVAL_MS),
      );
    }
  }

  await queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
};

export const useChatThreads = (token?: string, archived = false) => {
  const query = useInfiniteQuery({
    queryKey: ["chatThreads", token, archived] as const,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      listThreads(buildChatClient(token), {
        archived,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next,
  });

  const threads = useMemo(
    () => query.data?.pages.flatMap((page: ThreadsPage) => page.threads) ?? [],
    [query.data],
  );

  return { ...query, threads };
};

export const useSearchThreads = (
  token: string | undefined,
  queryText: string,
) => {
  return useQuery<ChatThreadDto[], Error>({
    queryKey: ["chatSearch", token, queryText],
    queryFn: () => searchThreads(buildChatClient(token), queryText),
    enabled: queryText.trim().length > 0,
    staleTime: 1000 * 60 * 5,
  });
};

export const useThreadMessages = (
  token: string | undefined,
  threadUuid: string | undefined,
) => {
  const query = useInfiniteQuery({
    queryKey: ["threadMessages", token, threadUuid] as const,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      replayThread(
        buildChatClient(token),
        threadUuid ?? "",
        pageParam ?? undefined,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next,
    enabled: Boolean(threadUuid),
  });

  const messages = useMemo(
    () => query.data?.pages.flatMap((page: ReplayPage) => page.messages) ?? [],
    [query.data],
  );

  return { ...query, messages };
};

export const useCreateChatThread = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation<InitResponse, Error, { content: string }>({
    mutationFn: ({ content }) =>
      initChatMessage(buildChatClient(token), { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
    },
  });
};

export const useSendChatMessage = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    InitResponse,
    Error,
    { threadUuid: string; content: string }
  >({
    mutationFn: ({ threadUuid, content }) =>
      initChatMessage(buildChatClient(token), {
        threadUuid,
        content,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["threadMessages"] });
      queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
    },
  });
};

export const useRegenerateChatMessage = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    InitResponse,
    Error,
    { threadUuid: string; content: string }
  >({
    mutationFn: ({ threadUuid, content }) =>
      initChatMessage(buildChatClient(token), {
        threadUuid,
        content,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["threadMessages"] });
      queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
    },
  });
};

export const useUpdateThreadTitle = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    ChatThreadDto,
    Error,
    { threadUuid: string; title: string }
  >({
    mutationFn: ({ threadUuid, title }) =>
      updateThread(buildChatClient(token), threadUuid, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
    },
  });
};

export const useArchiveThread = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation<ChatThreadDto, Error, { threadUuid: string }>({
    mutationFn: ({ threadUuid }) =>
      updateThread(buildChatClient(token), threadUuid, { is_archived: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
    },
  });
};

export const useDeleteThread = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { threadUuid: string }>({
    mutationFn: ({ threadUuid }) =>
      deleteThread(buildChatClient(token), threadUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatThreads"] });
      queryClient.invalidateQueries({ queryKey: ["threadMessages"] });
    },
  });
};

export interface ChatStreamOptions {
  /** When set, poll listThreads after the stream ends until this thread has a title. */
  pollTitleForThreadUuid?: string;
  token?: string;
}

export interface UseChatStreamResult {
  isStreaming: boolean;
  streamHandle: StreamHandle | null;
  streamEnvelopeExpiry: number | null;
  startStream: (
    initResponse: InitResponse,
    onEvent: (event: ChatStreamEvent) => void,
    onError: (error: any) => void,
    options?: ChatStreamOptions,
  ) => StreamHandle;
  stopStream: () => void;
}

export const useChatStream = (): UseChatStreamResult => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamHandle, setStreamHandle] = useState<StreamHandle | null>(null);
  const [streamEnvelopeExpiry, setStreamEnvelopeExpiry] = useState<
    number | null
  >(null);

  const queryClient = useQueryClient();
  const titlePollRef = useRef<ChatStreamOptions | null>(null);
  const titlePollInFlightRef = useRef(false);

  const runTitlePollIfNeeded = useCallback(async () => {
    const ctx = titlePollRef.current;
    titlePollRef.current = null;
    if (!ctx?.pollTitleForThreadUuid || titlePollInFlightRef.current) return;

    titlePollInFlightRef.current = true;
    try {
      await pollUntilThreadTitle(
        queryClient,
        ctx.token,
        ctx.pollTitleForThreadUuid,
      );
    } finally {
      titlePollInFlightRef.current = false;
    }
  }, [queryClient]);

  const stopStream = useCallback(() => {
    if (streamHandle) {
      streamHandle.close?.();
    }
    setStreamHandle(null);
    setIsStreaming(false);
    void runTitlePollIfNeeded();
  }, [streamHandle, runTitlePollIfNeeded]);

  const startStream = useCallback(
    (
      initResponse: InitResponse,
      onEvent: (event: ChatStreamEvent) => void,
      onError: (error: any) => void,
      options?: ChatStreamOptions,
    ) => {
      titlePollRef.current = options?.pollTitleForThreadUuid ? options : null;
      const expiryTime = new Date(initResponse.expires_at).getTime();
      const now = Date.now();
      const timeRemaining = expiryTime - now;

      if (timeRemaining <= 0) {
        const error = new Error(
          "Stream envelope expired before stream could open",
        );
        onError(error);
        return {
          close: () => {},
        };
      }

      if (timeRemaining < 5000) {
        console.warn(
          `Stream envelope expires in ${timeRemaining}ms - less than 5s`,
        );
      }

      const handle = openChatStream(
        initResponse,
        (event) => {
          if (event.type === "complete") {
            stopStream();
          }
          onEvent(event);
        },
        (error) => {
          setIsStreaming(false);
          setStreamHandle(null);
          titlePollRef.current = null;
          onError(error);
        },
      );

      setStreamHandle(handle);
      setIsStreaming(true);
      setStreamEnvelopeExpiry(expiryTime);

      return handle;
    },
    [runTitlePollIfNeeded],
  );

  useEffect(() => {
    return () => {
      if (streamHandle) {
        streamHandle.close();
      }
    };
  }, [streamHandle]);

  return {
    isStreaming,
    streamHandle,
    streamEnvelopeExpiry,
    startStream,
    stopStream,
  };
};

export const reconcileMessages = (
  localMessages: ChatMessage[],
  serverMessages: ChatMessageDto[],
): ChatMessage[] => {
  const serverMap = new Map(serverMessages.map((msg) => [msg.uuid, msg]));

  const reconciled: ChatMessage[] = localMessages.map((local) => {
    const server = serverMap.get(local.uuid);
    if (!server) {
      return local;
    }

    return {
      ...local,
      ...server,
      status: "complete",
      isOptimistic: false,
    };
  });

  for (const serverMessage of serverMessages) {
    if (!localMessages.some((local) => local.uuid === serverMessage.uuid)) {
      reconciled.push({
        ...serverMessage,
        status: "complete",
        isOptimistic: false,
      });
    }
  }

  return reconciled.sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
};
